import { Button, Card, Col, Row, Typography, Divider, Skeleton } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import useMultiNotification from "../../hooks/useNotification";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { getMyOrdersApi } from "../../redux-toolkit/slices/orderCreamSlice";
import { formatDateMonthDayYear } from "../../helpers/date";
import { orderStatusEnums } from "../../constants/orderConstants";
import { ReactComponent as RxIcon } from "../../graphics/svgs/rx.svg";

const ViewMyOrders = () => {
  const { contextHolder, openNotification } = useMultiNotification();
  const { isError, orders, isLoading } = useSelector(
    (state: RootState) => state.orderCream
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getMyOrdersApi());
  }, []);

  // Helper function to get status-specific message
  const getStatusMessage = (orderStatus: string, painRating: number) => {
    switch (orderStatus) {
      case orderStatusEnums.NEW:
        return `Dr. Shareef is reviewing your pain profile that you rated an ${painRating} to prescribe your formula.`;
      case orderStatusEnums.ASSIGNED_RX:
        return `Great news! Dr. Shareef prescreeeeeeeeeibed you a custom formula. We're hopeful it will help you better manage your current pain level ${painRating} pain rating.`;
      case orderStatusEnums.PRESCRIBED:
        return `Your formula has been prescribed based on your ${painRating} pain rating.`;
      case orderStatusEnums.NO_RX:
        return `After carefully reviewing your profile in detail, our physician determined that Twinge Health may not be the best fit for your specific needs at this time. Since no prescription was provided, you'll receive a full refund. We're here to help, please don't hesitate to contact us if you have any questions!`;
      case orderStatusEnums.REFUNDED:
        return `Thanks for trying Twinge Health! We're sorry it couldn't work out this time. Your refund has been processed. If your pain conditions change in the future, feel free to give us a try again!`;
      default:
        return `Processing your order with ${painRating} pain rating.`;
    }
  };

  // Helper function to get next steps message
  const getNextStepsMessage = (orderStatus: string, painRating: number) => {
    switch (orderStatus) {
      case orderStatusEnums.NEW:
        return `Within 24 hours your assessment will be reviewed and responded to.`;
      case orderStatusEnums.ASSIGNED_RX:
        return `Your formula is going to be compounded and shipped within the next 48 hours`;
      case orderStatusEnums.PRESCRIBED:
        return `Your formula has been prescribed based on your ${painRating} pain rating.`;
      case orderStatusEnums.NO_RX:
        return `We recommend you discuss options with your primary care provider.`;
      case orderStatusEnums.REFUNDED:
        return `We recommend you discuss options with your primary care provider.`;
      default:
        return `Processing your order with ${painRating} pain rating.`;
    }
  };

  // Skeleton loader component for order cards
  const OrderSkeleton = () => (
    <div
      className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12"
      style={{ backgroundColor: "var(--neutral-100)" }}
    >
      {/* Order Header Skeleton */}
      <div className="mb-4">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "8px",
          }}
        >
          <Skeleton.Input
            active
            size="small"
            style={{ width: 200, height: 32 }}
          />
          <Skeleton.Input
            active
            size="small"
            style={{ width: 120, height: 32 }}
          />
        </div>
      </div>

      {/* Status Update Skeleton */}
      <div className="mb-4">
        <Skeleton.Input
          active
          size="small"
          style={{ width: 150, height: 20, marginBottom: "8px" }}
        />
        <Skeleton paragraph={{ rows: 2 }} active />
      </div>

      {/* Formula Section Skeleton */}
      <div
        className="mb-4"
        style={{
          border: "1px solid var(--neutral-600)",
          borderRadius: "12px",
          padding: "16px",
          display: "inline-block",
          width: "fit-content",
          maxWidth: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",
          }}
        >
          <Skeleton.Avatar active size="small" />
          <Skeleton.Input
            active
            size="small"
            style={{ width: 180, height: 20 }}
          />
        </div>
        <Skeleton paragraph={{ rows: 3 }} active />
      </div>

      {/* Tracking Number Skeleton */}
      <div style={{ marginTop: "16px" }}>
        <Skeleton.Input
          active
          size="small"
          style={{ width: 120, height: 20, marginBottom: "8px" }}
        />
        <Skeleton.Input
          active
          size="small"
          style={{ width: 100, height: 16 }}
        />
      </div>

      {/* Next Steps Skeleton */}
      <div className="mt-4">
        <Skeleton.Input
          active
          size="small"
          style={{ width: 100, height: 20, marginBottom: "8px" }}
        />
        <Skeleton paragraph={{ rows: 1 }} active />
      </div>
    </div>
  );

  return (
    <>
      {contextHolder}
      <div className="common-section">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center py-8">
            {/* Header */}
            <div className="w-full mb-8 flex justify-center">
              <div className="login-form-container">
                <h1 className="h1 mb-4" style={{ textAlign: "left" }}>
                  My Orders
                </h1>
                <p
                  className="body1 mb-8"
                  style={{
                    color: "var(--theme-text-secondary)",
                    textAlign: "left",
                  }}
                >
                  View and manage your pain relief cream orders. Click on any
                  order to see detailed information.
                </p>
              </div>
            </div>

            {/* Orders Container */}
            <div className="w-full flex flex-col items-center gap-6">
              {isLoading ? (
                // Show skeleton loaders while loading
                <>
                  <OrderSkeleton />
                  <OrderSkeleton />
                </>
              ) : orders.length === 0 ? (
                // Show no orders message when not loading and no orders exist
                <div
                  className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 text-center"
                  style={{ backgroundColor: "var(--neutral-100)" }}
                >
                  <h3 className="h3 mb-4">No Orders Found</h3>
                  <p
                    className="body1"
                    style={{ color: "var(--theme-text-secondary)" }}
                  >
                    You haven't placed any orders yet. Start your pain relief
                    journey today!
                  </p>
                </div>
              ) : (
                // Show actual orders when loaded and orders exist
                orders.map((order, index) => {
                  const isUncompleted =
                    order.processingDetails?.orderStatus ===
                    orderStatusEnums.UNCOMPLETED;

                  const handleOrderClick = (e: React.MouseEvent) => {
                    e.preventDefault();
                    if (isUncompleted) {
                      // Navigate to OrderCream form with transactionId and step
                      const params = new URLSearchParams();
                      params.set("paymentId", order.transactionId || "");
                      if (order.currentStep !== undefined) {
                        params.set("step", order.currentStep.toString());
                      }
                      // Add shipping details if available
                      if (order.shippingDetails) {
                        if (order.shippingDetails.firstName)
                          params.set(
                            "firstName",
                            order.shippingDetails.firstName
                          );
                        if (order.shippingDetails.lastName)
                          params.set(
                            "lastName",
                            order.shippingDetails.lastName
                          );
                        if (order.shippingDetails.email)
                          params.set("email", order.shippingDetails.email);
                        if (order.shippingDetails.phoneNumber)
                          params.set(
                            "phoneNumber",
                            order.shippingDetails.phoneNumber
                          );
                        if (order.shippingDetails.address) {
                          if (order.shippingDetails.address.street1)
                            params.set(
                              "street1",
                              order.shippingDetails.address.street1
                            );
                          if (order.shippingDetails.address.street2)
                            params.set(
                              "street2",
                              order.shippingDetails.address.street2
                            );
                          if (order.shippingDetails.address.city)
                            params.set(
                              "city",
                              order.shippingDetails.address.city
                            );
                          if (order.shippingDetails.address.state)
                            params.set(
                              "state",
                              order.shippingDetails.address.state
                            );
                          if (order.shippingDetails.address.zipCode)
                            params.set(
                              "zipCode",
                              order.shippingDetails.address.zipCode
                            );
                        }
                      }
                      // Add selectedState if available
                      if (order.selectedState) {
                        params.set("state", order.selectedState);
                      }
                      console.log(params.toString());
                      navigate(`/order-cream?${params.toString()}`);
                    } else {
                      // Navigate to order details for completed orders
                      navigate(`/order-details/${order.id}`);
                    }
                  };

                  return (
                    <div
                      key={`order-card-${index}`}
                      onClick={handleOrderClick}
                      style={{ textDecoration: "none" }}
                      className="w-full flex justify-center"
                    >
                      <div
                        className="login-form-container rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 hover:shadow-lg transition-all duration-300 cursor-pointer"
                        style={{ backgroundColor: "var(--neutral-100)" }}
                      >
                        {/* SECTION 1: Order Header */}
                        <div className="mb-4">
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "8px",
                            }}
                          >
                            <Typography
                              className="h6 text-theme-text-secondary"
                              style={{
                                border: "1px solid var(--neutral-600)",
                                padding: "6px 16px",
                                borderRadius: "9999px",
                                lineHeight: "1",
                                display: "inline-block",
                                textAlign: "left",
                              }}
                            >
                              Order # {order.processingDetails.orderNumber} on{" "}
                              {formatDateMonthDayYear(order.createdAt)}
                            </Typography>
                            <span
                              className="text-xs font-medium"
                              style={{
                                backgroundColor: "var(--primary-100)",
                                color: "var(--primary-700)",
                                padding: "6px 16px",
                                borderRadius: "9999px",
                                lineHeight: "1",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  backgroundColor: "var(--primary-500)",
                                  borderRadius: "50%",
                                  animation: "pulse 2s infinite",
                                }}
                              ></span>
                              {order.processingDetails.orderStatus}
                            </span>
                          </div>
                        </div>

                        {/* SECTION 2: Status Update */}
                        <div className="mb-4">
                          <h4
                            className="h6"
                            style={{
                              color: "var(--theme-text-secondary)",
                              marginBottom: "1px",
                            }}
                          >
                            Here's the latest update:
                          </h4>
                          <p
                            className="caption1"
                            style={{ color: "var(--theme-text-primary)" }}
                          >
                            {getStatusMessage(
                              order.processingDetails.orderStatus,
                              order.painProfile.painRating
                            )}
                          </p>
                        </div>

                        {/* SECTION 3: Prescribed Formula - Only show for non-NEW statuses */}
                        {order.processingDetails.orderStatus !==
                          orderStatusEnums.NEW && (
                          <div
                            className="mb-4"
                            style={{
                              border: "1px solid var(--neutral-600)",
                              borderRadius: "12px",
                              padding: "16px",
                              display: "inline-block",
                              width: "fit-content",
                              maxWidth: "100%",
                            }}
                          >
                            <h4
                              className="h6"
                              style={{
                                color: "var(--theme-text-secondary)",
                                marginBottom: "1px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  borderRadius: "50%",
                                  border:
                                    "1px solid var(--theme-text-secondary)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <RxIcon
                                  style={{ width: "12px", height: "15px" }}
                                />
                              </div>
                              Custom formula for{" "}
                              {order.shippingDetails.firstName}
                            </h4>
                            <div style={{ color: "var(--theme-text-primary)" }}>
                              {order.processingDetails.formulaAssigned !==
                              null ? (
                                <>
                                  {order.processingDetails.formulaAssigned?.activeIngredients
                                    ?.split(",")
                                    .map((ingredient, index) => (
                                      <p
                                        key={index}
                                        className="h5"
                                        style={{
                                          marginBottom:
                                            index === 0 ? "1px" : "0",
                                        }}
                                      >
                                        {ingredient.trim()}
                                      </p>
                                    ))}
                                  <p
                                    className="h5"
                                    style={{ marginTop: "1px" }}
                                  >
                                    {
                                      order.processingDetails.formulaAssigned
                                        ?.creamBase
                                    }
                                  </p>
                                </>
                              ) : (
                                <p className="h5">---</p>
                              )}
                            </div>

                            {/* Instructions */}
                            {order.processingDetails.instructions && (
                              <div className="mt-3">
                                <p
                                  className="caption1"
                                  style={{ color: "var(--theme-text-primary)" }}
                                >
                                  <span
                                    className="h6"
                                    style={{
                                      color: "var(--theme-text-secondary)",
                                    }}
                                  >
                                    Directions for use:&nbsp;
                                  </span>
                                  {order.processingDetails.instructions}
                                </p>
                              </div>
                            )}

                            {/* Other Instructions */}
                            {order.processingDetails.otherInstructions && (
                              <div className="mt-3">
                                <p
                                  className="caption1"
                                  style={{ color: "var(--theme-text-primary)" }}
                                >
                                  <span
                                    className="h6"
                                    style={{
                                      color: "var(--theme-text-secondary)",
                                    }}
                                  >
                                    Directions for use:&nbsp;
                                  </span>
                                  {order.processingDetails.otherInstructions}
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* SECTION 4: Tracking Number - Only show for non-NEW statuses */}
                        {order.processingDetails.orderStatus !==
                          orderStatusEnums.NEW && (
                          <div style={{ marginTop: "16px" }}>
                            <h4
                              className="h6"
                              style={{
                                color: "var(--theme-text-secondary)",
                                marginBottom: "1px",
                              }}
                            >
                              Tracking Number
                            </h4>
                            <p
                              className="caption1"
                              style={{ color: "var(--theme-text-primary)" }}
                            >
                              {order.processingDetails.trackingNumber || "N/A"}
                            </p>
                          </div>
                        )}

                        {/* SECTION 5: Next Steps */}
                        <div className="mt-4">
                          <h4
                            className="h6"
                            style={{
                              color: "var(--theme-text-secondary)",
                              marginBottom: "1px",
                            }}
                          >
                            Next steps:
                          </h4>
                          <p
                            className="caption1"
                            style={{ color: "var(--theme-text-primary)" }}
                          >
                            {getNextStepsMessage(
                              order.processingDetails.orderStatus,
                              order.painProfile.painRating
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default ViewMyOrders;
